const DB_NAME = 'panaderia_offline'
const DB_VERSION = 4  // ← CAMBIADO a 4: añadido deliveries_history

class OfflineDB {
  constructor() {
    this.db = null
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = event.target.result

        // Clientes locales
        if (!db.objectStoreNames.contains('clients')) {
          db.createObjectStore('clients', { keyPath: 'id' })
        }
        
        // Productos locales
        if (!db.objectStoreNames.contains('products')) {
          db.createObjectStore('products', { keyPath: 'id' })
        }
        
        // Plantillas de clientes
        if (!db.objectStoreNames.contains('client_templates')) {
          const templateStore = db.createObjectStore('client_templates', { keyPath: 'id', autoIncrement: true })
          templateStore.createIndex('client_id', 'client_id', { unique: false })
        }

        // Operaciones pendientes (CRUD de clientes/productos)
        if (!db.objectStoreNames.contains('pending_operations')) {
          const opsStore = db.createObjectStore('pending_operations', { keyPath: 'id', autoIncrement: true })
          opsStore.createIndex('timestamp', 'timestamp', { unique: false })
          opsStore.createIndex('type', 'type', { unique: false })
        }

        // Albaranes offline
        if (!db.objectStoreNames.contains('deliveries_offline')) {
          const deliveriesStore = db.createObjectStore('deliveries_offline', { keyPath: 'id', autoIncrement: true })
          deliveriesStore.createIndex('client_id', 'client_id', { unique: false })
          deliveriesStore.createIndex('fecha', 'fecha', { unique: false })
          deliveriesStore.createIndex('synced', 'synced', { unique: false })
        }

        // ← AÑADIDO: Usuarios
        if (!db.objectStoreNames.contains('users')) {
          db.createObjectStore('users', { keyPath: 'id' })
        }

        // ← AÑADIDO: Historial de albaranes (últimos 14 días) para cobro offline del lunes
        if (!db.objectStoreNames.contains('deliveries_history')) {
          const histStore = db.createObjectStore('deliveries_history', { keyPath: 'id' })
          histStore.createIndex('client_id', 'client_id', { unique: false })
          histStore.createIndex('fecha', 'fecha', { unique: false })
          histStore.createIndex('client_fecha', ['client_id', 'fecha'], { unique: false })
        }
      }
    })
  }

  // CRUD Genérico
  async getAll(storeName) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly')
      const store = transaction.objectStore(storeName)
      const request = store.getAll()

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async get(storeName, key) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly')
      const store = transaction.objectStore(storeName)
      const request = store.get(key)

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async put(storeName, data) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)
      const request = store.put(data)

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async delete(storeName, key) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)
      const request = store.delete(key)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async clear(storeName) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)
      const request = store.clear()

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  // ==================== OPERACIONES PENDIENTES ====================
  
  async addPendingOperation(operation) {
    const op = {
      ...operation,
      timestamp: Date.now(),
      synced: false
    }
    return this.put('pending_operations', op)
  }

  async getPendingOperations() {
    const ops = await this.getAll('pending_operations')
    return ops.filter(o => !o.synced).sort((a, b) => a.timestamp - b.timestamp)
  }

  async markOperationSynced(id) {
    const op = await this.get('pending_operations', id)
    if (op) {
      op.synced = true
      await this.put('pending_operations', op)
    }
  }

  async clearSyncedOperations() {
    const ops = await this.getAll('pending_operations')
    for (const op of ops) {
      if (op.synced) {
        await this.delete('pending_operations', op.id)
      }
    }
  }

  // ==================== ALBARANES ====================
  
  async saveDeliveryOffline(deliveryData, existingDeliveryId = null) {
    const deliveryId = existingDeliveryId || `temp_${Date.now()}_${Math.random()}`
    const isUpdate = !!existingDeliveryId
    
    console.log(isUpdate ? '🔄 Actualizando albarán offline:' : '✨ Creando albarán offline:', deliveryId)
    
    const delivery = {
      id: deliveryId,
      client_id: deliveryData.client_id,
      fecha: deliveryData.fecha,
      ruta: deliveryData.ruta,
      estado: 'entregado',
      lines: deliveryData.lines || [],
      synced: false,
      timestamp: Date.now()
    }

    await this.put('deliveries_offline', delivery)

    await this.addPendingOperation({
      type: 'delivery',
      operation: isUpdate ? 'update' : 'insert',
      data: delivery
    })

    return deliveryId
  }

  async getDeliveriesOffline() {
    const deliveries = await this.getAll('deliveries_offline')
    return deliveries.filter(d => !d.synced)
  }

  async markDeliverySynced(id) {
    const delivery = await this.get('deliveries_offline', id)
    if (delivery) {
      delivery.synced = true
      await this.put('deliveries_offline', delivery)
    }
  }

  // ==================== CLIENTES ====================
  
  async cacheClients(clients) {
    await this.clear('clients')
    for (const client of clients) {
      await this.put('clients', client)
    }
  }

  async getCachedClients() {
    return this.getAll('clients')
  }

  async saveClientOffline(client, operationType) {
    // Guardar cliente en caché local
    if (operationType !== 'delete') {
      await this.put('clients', client)
    } else {
      await this.delete('clients', client.id)
    }

    // Registrar operación pendiente para sincronizar
    await this.addPendingOperation({
      type: 'client',
      operation: operationType, // 'insert', 'update', 'delete'
      data: client
    })
  }

  // ==================== PRODUCTOS ====================
  
  async cacheProducts(products) {
    await this.clear('products')
    for (const product of products) {
      await this.put('products', product)
    }
  }

  async getCachedProducts() {
    return this.getAll('products')
  }

  async saveProductOffline(product, operationType) {
    // Guardar producto en caché local
    if (operationType !== 'delete') {
      await this.put('products', product)
    } else {
      await this.delete('products', product.id)
    }

    // Registrar operación pendiente para sincronizar
    await this.addPendingOperation({
      type: 'product',
      operation: operationType, // 'insert', 'update', 'delete'
      data: product
    })
  }

  // ==================== PLANTILLAS ====================
  
  async cacheTemplates(templates) {
    await this.clear('client_templates')
    for (const template of templates) {
      await this.put('client_templates', template)
    }
  }

  async getCachedTemplates(clientId) {
    const transaction = this.db.transaction('client_templates', 'readonly')
    const store = transaction.objectStore('client_templates')
    const index = store.index('client_id')
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(clientId)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async saveTemplateOffline(template, operationType) {
    // Guardar plantilla en caché local
    if (operationType !== 'delete') {
      await this.put('client_templates', template)
    } else {
      await this.delete('client_templates', template.id)
    }

    // Registrar operación pendiente para sincronizar
    await this.addPendingOperation({
      type: 'template',
      operation: operationType,
      data: template
    })
  }

  async deleteTemplatesByClient(clientId) {
    const templates = await this.getCachedTemplates(clientId)
    for (const template of templates) {
      await this.delete('client_templates', template.id)
    }
  }

  // ==================== USUARIOS ====================
  
  async cacheUsers(users) {
    await this.clear('users')
    for (const user of users) {
      await this.put('users', user)
    }
  }

  async getCachedUsers() {
    return this.getAll('users')
  }

  // ==================== HISTORIAL DE ALBARANES (para cobro offline lunes) ====================

  /**
   * Guarda/actualiza albaranes históricos en caché local.
   * Cada delivery debe incluir su delivery_lines completo.
   */
  async cacheDeliveriesHistory(deliveries) {
    for (const delivery of deliveries) {
      await this.put('deliveries_history', delivery)
    }
  }

  /**
   * Devuelve albaranes del historial para un rango de fechas (todos los clientes).
   */
  async getDeliveriesHistory(fechaDesde, fechaHasta) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction('deliveries_history', 'readonly')
      const store = transaction.objectStore('deliveries_history')
      const index = store.index('fecha')
      const range = IDBKeyRange.bound(fechaDesde, fechaHasta)
      const request = index.getAll(range)

      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Devuelve albaranes del historial para un cliente y rango de fechas.
   */
  async getDeliveriesHistoryByClient(clientId, fechaDesde, fechaHasta) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction('deliveries_history', 'readonly')
      const store = transaction.objectStore('deliveries_history')
      const index = store.index('client_id')
      const request = index.getAll(clientId)

      request.onsuccess = () => {
        const all = request.result || []
        const filtered = all.filter(d => d.fecha >= fechaDesde && d.fecha <= fechaHasta)
        resolve(filtered)
      }
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Elimina albaranes del historial más antiguos que `diasMaximos` días.
   */
  async clearOldDeliveriesHistory(diasMaximos = 21) {
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - diasMaximos)
    const fechaLimite = cutoff.toISOString().slice(0, 10)

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction('deliveries_history', 'readwrite')
      const store = transaction.objectStore('deliveries_history')
      const index = store.index('fecha')
      const range = IDBKeyRange.upperBound(fechaLimite, true)
      const request = index.openCursor(range)

      request.onsuccess = (event) => {
        const cursor = event.target.result
        if (cursor) {
          cursor.delete()
          cursor.continue()
        } else {
          resolve()
        }
      }
      request.onerror = () => reject(request.error)
    })
  }
}

export const offlineDB = new OfflineDB()